import { computed, ref, toValue, watchEffect, type MaybeRefOrGetter, type Ref } from 'vue'
import QRCode from 'qrcode'
import type { QRCodeToDataURLOptions } from 'qrcode'

export type QrErrorCorrection = 'L' | 'M' | 'Q' | 'H';

export interface UseQrCodeOptions {
  /** Pixel width/height of the generated PNG. Default 196. */
  size?: MaybeRefOrGetter<number>;
  /** Quiet-zone modules. Default 1. */
  margin?: MaybeRefOrGetter<number>;
  /** Module color. Default EagleFineweb green. */
  color?: MaybeRefOrGetter<string>;
  /** Background color. Default white. */
  background?: MaybeRefOrGetter<string>;
  errorCorrection?: MaybeRefOrGetter<QrErrorCorrection>;
}

export interface UseQrCodeReturn {
  /** data:image/png;base64,... ready for <img :src="src"> */
  qrsrc: Ref<string>;
  qrpending: Ref<boolean>;
  qrerror: Ref<Error | null>;
  qralt: Ref<string>;
}

const DEFAULT_COLOR = '#0B3D2E'
const DEFAULT_BG = '#ffffff'

function toQrOptions (
  size: number,
  margin: number,
  color: string,
  background: string,
  errorCorrection: QrErrorCorrection,
): QRCodeToDataURLOptions {
  return {
    width: size,
    margin,
    errorCorrectionLevel: errorCorrection,
    color: { dark: color, light: background },
  }
}

/**
 * Vue 3 composable: encode `value` as a QR PNG data URL.
 *
 * @example
 * const payload = ref('https://eaglefineweb.example/login')
 * const { qrsrc, qrpending, qrerror } = useQrCode(payload, { size: 196 })
 */
export function useQrCode (
  value: MaybeRefOrGetter<string>,
  options: UseQrCodeOptions = {},
): UseQrCodeReturn {
  const qrsrc = ref('')
  const qrpending = ref(false)
  const qrerror = ref<Error | null>(null)

  const qralt = computed(() => {
    const text = toValue(value)
    return text ? `QR code for ${text}` : 'QR code'
  })

  watchEffect(async (onCleanup) => {
    const text = toValue(value).trim()
    const size = toValue(options.size) ?? 196
    const margin = toValue(options.margin) ?? 1
    const color = toValue(options.color) ?? DEFAULT_COLOR
    const background = toValue(options.background) ?? DEFAULT_BG
    const errorCorrection = toValue(options.errorCorrection) ?? 'M'

    if (!text) {
      qrsrc.value = ''
      qrerror.value = null
      qrpending.value = false
      return
    }

    let cancelled = false
    onCleanup(() => {
      cancelled = true
    })

    qrpending.value = true
    qrerror.value = null

    try {
      const dataUrl = await QRCode.toDataURL(
        text,
        toQrOptions(size, margin, color, background, errorCorrection),
      )
      if (cancelled) return
      qrsrc.value = dataUrl
    } catch (err) {
      if (cancelled) return
      qrsrc.value = ''
      qrerror.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      if (!cancelled) qrpending.value = false
    }
  })

  return { qrsrc, qrpending, qrerror, qralt }
}
